import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { ChevronLeft, ChevronRight } from '@untitled-ui/icons-react'
import SvgIcon from '@mui/material/SvgIcon'
import useTranslate from '@/hooks/use-translate'
import { tokens } from '@/locales/tokens'
import type { Release } from '../types'
import {
  whatsNewCarouselDotSx,
  whatsNewCarouselHeaderSx,
  whatsNewCarouselNavButtonSx,
  whatsNewCarouselPagerBoldSx,
  whatsNewCarouselPagerTextSx,
} from '../whatsNewModalStyles'
import { ReleaseBlock } from './ReleaseBlock'

type ReleasesCarouselProps = {
  releases: Release[]
  onCurrentReleaseChange?: (release: Release) => void
}

export const ReleasesCarousel: FC<ReleasesCarouselProps> = ({
  releases,
  onCurrentReleaseChange,
}) => {
  const { t } = useTranslate('whatsNew')
  const [idx, setIdx] = useState(0)
  const total = releases.length
  const release = releases[idx]
  const isLatest = idx === 0
  const singleRelease = total <= 1

  useEffect(() => {
    if (release) {
      onCurrentReleaseChange?.(release)
    }
  }, [release, onCurrentReleaseChange])

  useEffect(() => {
    setIdx((i) => {
      if (total === 0) return 0
      return Math.min(i, total - 1)
    })
  }, [total, releases])

  const go = (delta: number) => {
    setIdx((i) => Math.min(total - 1, Math.max(0, i + delta)))
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box sx={whatsNewCarouselHeaderSx}>
        <Button
          onClick={() => go(-1)}
          disabled={idx === 0 || singleRelease}
          aria-label={t('carouselNewer')}
          sx={whatsNewCarouselNavButtonSx(idx === 0 || singleRelease)}
        >
          <SvgIcon>
            <ChevronLeft />
          </SvgIcon>
        </Button>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            minWidth: 0,
          }}
        >
          <Typography sx={whatsNewCarouselPagerTextSx}>
            {isLatest && (
              <Box
                component="span"
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '.4px',
                  padding: '2px 7px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                }}
              >
                {t('latest')}
              </Box>
            )}
            <Trans
              i18nKey={tokens.whatsNew.releasePager}
              values={{ current: idx + 1, total }}
              components={{
                bold: <Box component="span" sx={whatsNewCarouselPagerBoldSx} />,
              }}
            />
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.625, alignItems: 'center' }}>
            {releases.map((r, i) => (
              <Button
                key={r.releaseId}
                onClick={() => setIdx(i)}
                aria-label={t('goToVersion', { version: r.version })}
                sx={whatsNewCarouselDotSx(i === idx)}
              />
            ))}
          </Box>
        </Box>

        <Button
          onClick={() => go(1)}
          disabled={idx === total - 1 || singleRelease}
          aria-label={t('carouselOlder')}
          sx={whatsNewCarouselNavButtonSx(idx === total - 1 || singleRelease)}
        >
          <SvgIcon>
            <ChevronRight />
          </SvgIcon>
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '20px 24px 24px',
        }}
      >
        {release ? (
          <ReleaseBlock key={release.releaseId} release={release} />
        ) : null}
      </Box>
    </Box>
  )
}
